import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Sparkles,
  Database,
  Loader2,
  FileText,
  Search,
  Edit3,
  Trash2,
  CheckCircle,
  Clock,
  Target,
  ChevronDown,
  ChevronUp,
  Zap,
  AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, Badge } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { DashboardSkeleton } from '../../components/LoadingSkeleton';
import { GeneratedQuestionsPreview } from '../../components/GeneratedQuestionsPreview';
import { useToast } from '../../contexts/ToastContext';
import { pdfAnalysisService } from '../../services/pdf-analysis.service';
import { questionsService } from '../../services/questions.service';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '../../utils/deleteConfirmation';
import type { PdfAnalysis } from '../../types/pdf-analysis.types';
import { formatAlternativeText } from '../../utils/textFormatting';

type TabId = 'generate' | 'questions';

const EXAMS = [
  'AI', 'CPA', 'CPro-R', 'CPro-I', 'CGA', 'CGE', 'CFG',
  'RPPS-BASICO', 'RPPS-INTERMEDIARIO', 'RPPS-AVANCADO',
  'CNPI-CB', 'CNPI-CG', 'CNPI-CT', 'CNPI-PLENO', 'CNPI-FUNDAMENTALISTA',
  'CFP-MOD1', 'CFP-MOD2', 'CFP-MOD3',
  'ICSS-ADM', 'ICSS-INVEST',
  'CA-600', 'CA-600-HIS', 'CA-300-400', 'CA-300-400-HIS',
  'PQO-OPERACOES', 'PQO-COMERCIAL', 'PQO-COMPLIANCE', 'PQO-RISCO',
  'PQO-BACKOFFICE', 'PQO-LIQUIDACAO', 'PQO-CUSTODIA',
  'FBB-OUVIDORIA',
];

const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'MCQ' },
  { value: 'short_answer', label: 'Short Answer' },
  { value: 'case_study', label: 'Case Study' },
  { value: 'interactive', label: 'Interactive' },
];

const DIFFICULTIES = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

export function PdfAnalysisQuestions() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('generate');

  // Fetch the PDF analysis
  const { data: analysis, isLoading } = useQuery<PdfAnalysis>({
    queryKey: ['pdf-analysis', id],
    queryFn: () => pdfAnalysisService.getAnalysis(id!),
    enabled: !!id,
  });

  if (isLoading) return <DashboardSkeleton />;

  if (!analysis || analysis.status !== 'completed') {
    return (
      <div className="space-y-6 animate-in">
        <PageHeader
          title="Questions"
          breadcrumbs={[
            { label: 'PDF Analysis', path: '/pdf-analysis' },
            { label: 'Questions' },
          ]}
        />
        <div className="card">
          <EmptyState
            icon={AlertTriangle}
            title="Analysis not ready"
            description="The PDF analysis must be completed before generating questions."
            action={{ label: 'Back to Analysis', onClick: () => navigate(`/pdf-analysis/${id}`) }}
          />
        </div>
      </div>
    );
  }

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: 'generate', label: 'Generate Questions', icon: Sparkles },
    { id: 'questions', label: 'Existing Questions', icon: Database },
  ];

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="Questions"
          description={`${analysis.filename} \u2014 ${analysis.totalItems} topics in ${analysis.extractionMode} mode`}
          breadcrumbs={[
            { label: 'PDF Analysis', path: '/pdf-analysis' },
            { label: analysis.filename, path: `/pdf-analysis/${id}` },
            { label: 'Questions' },
          ]}
        />
        <button
          onClick={() => navigate(`/pdf-analysis/${id}`)}
          className="btn-secondary flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex-1 justify-center ${
              activeTab === tab.id
                ? 'bg-white text-[#1e73be] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'generate' && (
        <GenerateTab
          analysis={analysis}
          onGenerated={() => {
            setActiveTab('questions');
            queryClient.invalidateQueries({ queryKey: ['analysis-questions'] });
          }}
        />
      )}
      {activeTab === 'questions' && (
        <QuestionsTab analysis={analysis} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1: Generate Questions
// ═══════════════════════════════════════════════════════════════════════════════

function GenerateTab({
  analysis,
  onGenerated,
}: {
  analysis: PdfAnalysis;
  onGenerated: () => void;
}) {
  const { showToast } = useToast();

  // Form state
  const [exam, setExam] = useState('CPA');
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [difficulty, setDifficulty] = useState('medium');
  const [count, setCount] = useState(5);
  const [mcqFormat, setMcqFormat] = useState<'anbima' | 'short'>('anbima');
  const [selectedOeCodes, setSelectedOeCodes] = useState<string[]>([]);
  const [customPrompt, setCustomPrompt] = useState('');

  // Generation result
  const [generatedQuestions, setGeneratedQuestions] = useState<any[] | null>(null);

  // Get unique OE codes from analysis
  const oeCodes = useMemo(() => {
    const codes = new Map<string, { code: string; text: string; count: number }>();
    for (const item of analysis.items || []) {
      const existing = codes.get(item.oeCode);
      if (existing) {
        existing.count++;
      } else {
        codes.set(item.oeCode, { code: item.oeCode, text: item.oeText || '', count: 1 });
      }
    }
    return Array.from(codes.values());
  }, [analysis.items]);

  const toggleOeCode = (code: string) => {
    setSelectedOeCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // Build text content from selected topics
  const buildTextContent = () => {
    let items = analysis.items || [];
    if (selectedOeCodes.length > 0) {
      items = items.filter((i) => selectedOeCodes.includes(i.oeCode));
    }
    return items
      .map((item) => {
        const parts = [`Topic: ${item.topics}`, `OE: ${item.oeCode}`];
        if (item.description) parts.push(`Description: ${item.description}`);
        if (item.concepts?.length) parts.push(`Concepts: ${item.concepts.join(', ')}`);
        if (item.keywords?.length) parts.push(`Keywords: ${item.keywords.join(', ')}`);
        if (item.formulas?.length)
          parts.push(`Formulas: ${item.formulas.map((f) => `${f.name}: ${f.expression}`).join('; ')}`);
        if (item.examAlerts?.length) parts.push(`Exam Alerts: ${item.examAlerts.join('; ')}`);
        return parts.join('\n');
      })
      .join('\n\n---\n\n');
  };

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      const text = buildTextContent();
      if (!text.trim()) throw new Error('No topics selected for generation');

      const response = await questionsService.generateFromText(text, {
        exam,
        count,
        questionType,
        difficulty,
        format: mcqFormat,
        oeCode: selectedOeCodes.length === 1 ? selectedOeCodes[0] : undefined,
        customPrompt: customPrompt || undefined,
      });
      return response;
    },
    onSuccess: (response) => {
      const questions = response?.data?.questions || response?.questions || [];
      if (questions.length === 0) {
        showToast('warning', 'No Questions', 'AI did not return any questions. Try adjusting parameters.');
        return;
      }
      // Add tempId for preview component
      const withIds = questions.map((q: any, i: number) => ({
        ...q,
        tempId: q.tempId || `gen-${Date.now()}-${i}`,
      }));
      setGeneratedQuestions(withIds);
      showToast('success', 'Questions Generated', `${withIds.length} questions generated successfully.`);
    },
    onError: (error: any) => {
      showToast('error', 'Generation Failed', error.response?.data?.error || error.message);
    },
  });

  // Save handler from preview
  const handleSave = async (questions: any[]) => {
    try {
      await questionsService.batchSaveQuestions(questions);
      showToast('success', 'Questions Saved', `${questions.length} questions saved to the Question Bank.`);
      setGeneratedQuestions(null);
      onGenerated();
    } catch (error: any) {
      showToast('error', 'Save Failed', error.response?.data?.error || error.message);
    }
  };

  // If showing preview
  if (generatedQuestions) {
    return (
      <GeneratedQuestionsPreview
        questions={generatedQuestions}
        onSave={handleSave}
        onCancel={() => setGeneratedQuestions(null)}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* OE Code selection */}
      <div className="card">
        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-[#1e73be]" />
          Select Topics (OE Codes)
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Choose which OE codes to include. Leave empty to use all topics.
        </p>
        <div className="flex flex-wrap gap-2">
          {oeCodes.map((oe) => (
            <button
              key={oe.code}
              onClick={() => toggleOeCode(oe.code)}
              className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${
                selectedOeCodes.includes(oe.code)
                  ? 'border-[#1e73be] bg-blue-50 text-[#1e73be]'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <span className="font-mono font-bold">{oe.code}</span>
              <span className="text-xs text-gray-400 ml-1.5">({oe.count})</span>
            </button>
          ))}
        </div>
        {selectedOeCodes.length > 0 && (
          <button
            onClick={() => setSelectedOeCodes([])}
            className="text-xs text-red-500 hover:text-red-700 font-medium mt-2"
          >
            Clear selection
          </button>
        )}
      </div>

      {/* Configuration */}
      <div className="card">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#ea7500]" />
          Generation Settings
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Exam */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Exam</label>
            <select
              value={exam}
              onChange={(e) => setExam(e.target.value)}
              className="input-field w-full"
            >
              {EXAMS.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>

          {/* Question Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Question Type</label>
            <select
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value)}
              className="input-field w-full"
            >
              {QUESTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Difficulty */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Difficulty</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="input-field w-full"
            >
              {DIFFICULTIES.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          {/* Count */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Number of Questions
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
              className="input-field w-full"
            />
          </div>

          {/* MCQ Format */}
          {questionType === 'multiple_choice' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">MCQ Format</label>
              <select
                value={mcqFormat}
                onChange={(e) => setMcqFormat(e.target.value as 'anbima' | 'short')}
                className="input-field w-full"
              >
                <option value="anbima">ANBIMA (750-850 chars)</option>
                <option value="short">Short (30-500 chars)</option>
              </select>
            </div>
          )}
        </div>

        {/* Custom Prompt */}
        <div className="mt-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Custom Prompt <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Add specific instructions for question generation..."
            rows={3}
            className="input-field w-full resize-none"
          />
        </div>
      </div>

      {/* Generate Button */}
      <div className="flex justify-end">
        <button
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
          className="btn-primary px-8 py-3 rounded-xl font-semibold text-sm flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generateMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Generate {count} Questions
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2: Existing Questions
// ═══════════════════════════════════════════════════════════════════════════════

function QuestionsTab({ analysis }: { analysis: PdfAnalysis }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Get all OE codes from the analysis
  const analysisCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const item of analysis.items || []) {
      codes.add(item.oeCode);
    }
    return Array.from(codes);
  }, [analysis.items]);

  // Fetch questions matching this analysis's OE codes
  const { data: allQuestions = [], isLoading } = useQuery({
    queryKey: ['analysis-questions', analysisCodes],
    queryFn: async () => {
      if (analysisCodes.length === 0) return [];
      // Fetch all questions, filter by OE codes on client
      const response = await questionsService.getQuestions({ limit: 10000 });
      const questions = response?.data?.questions || [];
      return questions.filter((q: any) => {
        const qCode = q.oeCode || '';
        const qCodes = q.oeCodes || [];
        return (
          analysisCodes.includes(qCode) ||
          qCodes.some((c: string) => analysisCodes.includes(c))
        );
      });
    },
    enabled: analysisCodes.length > 0,
  });

  // Filter questions
  const filtered = useMemo(() => {
    let result = allQuestions;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (q: any) =>
          (q.stem || q.question || '').toLowerCase().includes(term) ||
          (q.oeCode || '').toLowerCase().includes(term)
      );
    }
    if (difficultyFilter !== 'all') {
      result = result.filter((q: any) => q.difficulty === difficultyFilter);
    }
    if (typeFilter !== 'all') {
      result = result.filter((q: any) => q.type === typeFilter);
    }
    return result;
  }, [allQuestions, searchTerm, difficultyFilter, typeFilter]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (qId: string) => questionsService.deleteQuestion(qId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analysis-questions'] });
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      showDeleteSuccess('Question deleted successfully.');
    },
    onError: (error: any) => {
      showDeleteError(error.response?.data?.error || 'Failed to delete question');
    },
  });

  const handleDelete = async (qId: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Question?',
    });

    if (confirmed) {
      deleteMutation.mutate(qId);
    }
  };

  // Stats
  const totalQ = allQuestions.length;
  const approvedQ = allQuestions.filter((q: any) => q.status === 'approved').length;
  const draftQ = allQuestions.filter(
    (q: any) => q.status === 'draft' || q.status === 'generated'
  ).length;

  return (
    <div className="space-y-5">
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-100 text-blue-600">
            <Database className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-700">{totalQ}</p>
            <p className="text-xs text-gray-500">Total Questions</p>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-green-100/50 rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-green-100 text-green-600">
            <CheckCircle className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-green-700">{approvedQ}</p>
            <p className="text-xs text-gray-500">Approved</p>
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-orange-100/50 rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-amber-100 text-amber-600">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-700">{draftQ}</p>
            <p className="text-xs text-gray-500">Drafts</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field w-full pl-9"
            />
          </div>
          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            className="input-field"
          >
            <option value="all">All Difficulties</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="input-field"
          >
            <option value="all">All Types</option>
            <option value="multiple_choice">MCQ</option>
            <option value="MCQ">MCQ (Legacy)</option>
            <option value="short_answer">Short Answer</option>
            <option value="case_study">Case Study</option>
            <option value="interactive">Interactive</option>
          </select>
        </div>
      </div>

      {/* Question List */}
      {isLoading ? (
        <DashboardSkeleton />
      ) : filtered.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={Database}
            title="No questions found"
            description={
              totalQ === 0
                ? 'No questions have been generated for these OE codes yet. Switch to the Generate tab to create some.'
                : 'No questions match your current filters.'
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((q: any) => (
            <QuestionCard
              key={q._id}
              question={q}
              expanded={expandedId === q._id}
              onToggle={() => setExpandedId(expandedId === q._id ? null : q._id)}
              onEdit={() => navigate(`/questions/${q._id}/edit`)}
              onDelete={() => handleDelete(q._id)}
            />
          ))}
          <p className="text-xs text-gray-400 text-center pt-2">
            Showing {filtered.length} of {totalQ} questions
          </p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Question Card
// ═══════════════════════════════════════════════════════════════════════════════

function QuestionCard({
  question: q,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}: {
  question: any;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const stem = q.stem || q.question || q.question_text || '';
  const diffMap: Record<string, string> = { easy: 'success', medium: 'warning', hard: 'error' };
  const typeLabels: Record<string, string> = {
    multiple_choice: 'MCQ',
    MCQ: 'MCQ',
    short_answer: 'Short Answer',
    SHORT: 'Short Answer',
    case_study: 'Case Study',
    case_based: 'Case Study',
    interactive: 'Interactive',
    CALC: 'Calculation',
    EXPLANATORY: 'Explanatory',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-start gap-3 text-left"
      >
        <div className="flex-1 min-w-0">
          {/* Question ID */}
          {q._id && (
            <div className="mb-1.5">
              <span className="font-mono text-[10px] font-medium text-gray-400">
                ID: {q._id}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            {q.oeCode && (
              <span className="font-mono text-xs font-bold text-[#1e73be] bg-blue-50 px-2 py-0.5 rounded">
                {q.oeCode}
              </span>
            )}
            <Badge variant={(diffMap[q.difficulty] || 'neutral') as any} size="sm">
              {q.difficulty}
            </Badge>
            <Badge variant="info" size="sm">
              {typeLabels[q.type] || q.type}
            </Badge>
            <StatusBadge status={q.status || 'draft'} />
          </div>
          <p className="text-sm text-gray-900 line-clamp-2">{stem}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(q.createdAt).toLocaleDateString()}
            </span>
            {q.examName && <span>{q.examName}</span>}
            {q.validationScore != null && (
              <span className="flex items-center gap-1">
                <Target className="w-3 h-3" />
                {q.validationScore}/10
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onEdit}
            className="p-2 text-gray-400 hover:text-[#1e73be] hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <div className="p-2 text-gray-300">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </button>

      {/* Expanded */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3 animate-in">
          {/* Question ID - Expanded View */}
          {q._id && (
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-xs font-semibold text-gray-600">Question ID:</span>
              <code className="text-xs font-mono text-gray-900 bg-white px-2 py-1 rounded border border-gray-200">
                {q._id}
              </code>
            </div>
          )}

          {/* Full question text */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">Question</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{stem}</p>
          </div>

          {/* Alternatives */}
          {q.alternatives?.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-1.5">Alternatives</p>
              <div className="space-y-1.5">
                {q.alternatives.map((alt: any, i: number) => (
                  <div
                    key={i}
                    className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 border ${
                      alt.isCorrect
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-gray-50 border-gray-100 text-gray-700'
                    }`}
                  >
                    <span className="font-bold flex-shrink-0">{alt.letter || String.fromCharCode(65 + i)}.</span>
                    <span className="flex-1">{formatAlternativeText(alt.text)}</span>
                    {alt.isCorrect && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Explanation */}
          {q.explanation && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-1">Explanation</p>
              <p className="text-sm text-gray-700 bg-blue-50/50 rounded-lg px-3 py-2 border border-blue-100">
                {q.explanation}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PdfAnalysisQuestions;
